import React, { useState, useEffect, useMemo } from 'react';
import { 
  FileText, Download, Filter, Package, Users, AlertTriangle, Calendar,
  TrendingUp, TrendingDown, ArrowUp, ArrowDown, ArrowUpDown, Search, X,
  FileSpreadsheet, FileType, ChevronDown
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const API = process.env.REACT_APP_BACKEND_URL;

// SearchInput Component
const SearchInput = ({ value, onChange, placeholder, className = "" }) => (
  <div className={`relative ${className}`}>
    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-stone-400" />
    <Input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="pl-10 pr-8"
    />
    {value && (
      <button onClick={() => onChange('')} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-stone-400 hover:text-stone-600">
        <X className="w-4 h-4" />
      </button>
    )}
  </div>
);

// SortableHeader Component
const SortableHeader = ({ column, label, sortConfig, onSort, className = "" }) => {
  const isSorted = sortConfig?.key === column;
  const direction = sortConfig?.direction;
  const handleClick = () => {
    let newDirection = 'asc';
    if (isSorted) {
      if (direction === 'asc') newDirection = 'desc';
      else if (direction === 'desc') newDirection = null;
    }
    onSort(newDirection ? { key: column, direction: newDirection } : null);
  };
  return (
    <TableHead className={`cursor-pointer hover:bg-stone-50 select-none ${className}`} onClick={handleClick}>
      <div className="flex items-center gap-1">
        <span>{label}</span>
        {isSorted ? (direction === 'asc' ? <ArrowUp className="w-4 h-4 text-lime-600" /> : <ArrowDown className="w-4 h-4 text-lime-600" />) : <ArrowUpDown className="w-4 h-4 text-stone-300" />}
      </div>
    </TableHead>
  );
};

// Utility functions
const sortData = (data, sortConfig) => {
  if (!sortConfig?.key) return data;
  return [...data].sort((a, b) => {
    let aVal = a[sortConfig.key], bVal = b[sortConfig.key];
    if (aVal == null) return sortConfig.direction === 'asc' ? 1 : -1;
    if (bVal == null) return sortConfig.direction === 'asc' ? -1 : 1;
    if (typeof aVal === 'string') aVal = aVal.toLowerCase();
    if (typeof bVal === 'string') bVal = bVal.toLowerCase();
    if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });
};

const filterData = (data, searchTerm, searchFields) => {
  if (!searchTerm?.trim()) return data;
  const term = searchTerm.toLowerCase().trim();
  return data.filter(item => searchFields.some(field => {
    const value = item[field];
    return value != null && String(value).toLowerCase().includes(term);
  }));
};

export const InventoryReports = () => {
  const [activeReport, setActiveReport] = useState('current-stock');
  const [inventory, setInventory] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [products, setProducts] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // All Warehouses view
  const [viewAllWarehouses, setViewAllWarehouses] = useState(false);
  const [allWarehousesData, setAllWarehousesData] = useState(null);
  const [loadingAllWarehouses, setLoadingAllWarehouses] = useState(false);
  
  // Filters
  const [productFilter, setProductFilter] = useState('');
  const [vendorFilter, setVendorFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'product_name', direction: 'asc' });
  
  // Low stock threshold
  const LOW_STOCK_THRESHOLD = 500;

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (viewAllWarehouses) {
      fetchAllWarehousesData();
    }
  }, [viewAllWarehouses]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [invRes, transRes, prodRes, vendRes] = await Promise.all([
        axios.get(`${API}/api/inventory`, { withCredentials: true }),
        axios.get(`${API}/api/transactions`, { withCredentials: true }),
        axios.get(`${API}/api/products`, { withCredentials: true }),
        axios.get(`${API}/api/vendors`, { withCredentials: true })
      ]);
      setInventory(invRes.data || []);
      setTransactions(transRes.data || []);
      setProducts(prodRes.data || []);
      setVendors(vendRes.data || []);
    } catch (error) {
      toast.error("Failed to fetch report data");
    } finally {
      setLoading(false);
    }
  };

  const fetchAllWarehousesData = async () => {
    setLoadingAllWarehouses(true);
    try {
      const [inventoryRes, summaryRes] = await Promise.all([
        axios.get(`${API}/api/reports/all-warehouses/inventory`, { withCredentials: true }),
        axios.get(`${API}/api/reports/all-warehouses/summary`, { withCredentials: true })
      ]);
      setAllWarehousesData({
        inventory: inventoryRes.data || [],
        summary: summaryRes.data || { warehouses: [], totals: {} }
      });
    } catch (error) {
      toast.error("Failed to fetch all-warehouses data. You may not have permission.");
      setViewAllWarehouses(false);
    } finally {
      setLoadingAllWarehouses(false);
    }
  };

  // Current Stock Report data
  const currentStockData = useMemo(() => {
    let data = inventory.map(item => ({
      ...item,
      opening_stock: item.opening_stock || 0,
      total_in: item.in_stock || 0,
      total_out: item.out_stock || 0,
      current_balance: item.current_stock || 0
    }));
    data = filterData(data, searchTerm, ['product_name', 'category', 'vendor']);
    return sortData(data, sortConfig);
  }, [inventory, searchTerm, sortConfig]);

  // Product-wise Report data
  const productWiseData = useMemo(() => {
    if (!productFilter) return [];
    const product = products.find(p => p.id === productFilter);
    if (!product) return [];
    
    const productTrans = transactions.filter(t => t.product_id === productFilter);
    const inTrans = productTrans.filter(t => t.transaction_type === 'in');
    const outTrans = productTrans.filter(t => t.transaction_type === 'out');
    
    const invItem = inventory.find(i => i.product_id === productFilter);
    
    return [{
      product_name: product.product_name,
      category: product.category,
      opening_quantity: invItem?.opening_stock || 0,
      purchases: inTrans.reduce((sum, t) => sum + t.quantity, 0),
      sales: outTrans.reduce((sum, t) => sum + t.quantity, 0),
      adjustments: 0,
      closing_quantity: invItem?.current_stock || 0
    }];
  }, [productFilter, products, transactions, inventory]);

  // Vendor-wise Report data
  const vendorWiseData = useMemo(() => {
    const vendorMap = {};
    transactions.filter(t => t.transaction_type === 'in').forEach(t => {
      const key = t.vendor_id || 'unknown';
      if (!vendorMap[key]) {
        vendorMap[key] = {
          vendor_id: key,
          vendor_name: t.vendor_name || 'Unknown',
          total_quantity: 0,
          products: new Set(),
          transaction_count: 0
        };
      }
      vendorMap[key].total_quantity += t.quantity;
      vendorMap[key].products.add(t.product_name);
      vendorMap[key].transaction_count++;
    });
    
    let data = Object.values(vendorMap).map(v => ({
      ...v,
      products: Array.from(v.products).join(', '),
      product_count: v.products.size || Array.from(v.products).length
    }));
    
    if (vendorFilter) {
      data = data.filter(v => v.vendor_id === vendorFilter);
    }
    data = filterData(data, searchTerm, ['vendor_name', 'products']);
    return sortData(data, sortConfig);
  }, [transactions, vendorFilter, searchTerm, sortConfig]);

  // Low Stock Report data
  const lowStockData = useMemo(() => {
    let data = inventory.filter(item => item.current_stock < LOW_STOCK_THRESHOLD);
    data = filterData(data, searchTerm, ['product_name', 'category']);
    return sortData(data, sortConfig);
  }, [inventory, searchTerm, sortConfig]);

  // Date-wise Report data
  const dateWiseData = useMemo(() => {
    let data = transactions;
    
    if (startDate) {
      data = data.filter(t => new Date(t.transaction_date) >= new Date(startDate));
    }
    if (endDate) {
      data = data.filter(t => new Date(t.transaction_date) <= new Date(endDate + 'T23:59:59'));
    }
    
    data = filterData(data, searchTerm, ['product_name', 'vendor_name', 'notes']);
    return sortData(data, { key: 'transaction_date', direction: 'desc' });
  }, [transactions, startDate, endDate, searchTerm]);

  // Export functions
  const exportToExcel = (data, filename) => {
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Report');
    XLSX.writeFile(wb, `${filename}_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success('Excel exported successfully');
  };

  const exportToCSV = (data, filename) => {
    const ws = XLSX.utils.json_to_sheet(data);
    const csv = XLSX.utils.sheet_to_csv(ws);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('CSV exported successfully');
  };

  const exportToPDF = (data, filename, columns) => {
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text(filename.replace(/_/g, ' '), 14, 15);
    doc.setFontSize(10);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 22);
    
    const tableData = data.map(row => columns.map(col => row[col.key] ?? ''));
    
    autoTable(doc, {
      head: [columns.map(col => col.label)],
      body: tableData,
      startY: 28,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [77, 124, 15] }
    });
    
    doc.save(`${filename}_${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success('PDF exported successfully');
  };

  // Export Dropdown State
  const [exportDropdownOpen, setExportDropdownOpen] = useState({});

  const toggleExportDropdown = (key) => {
    setExportDropdownOpen(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const closeExportDropdown = (key) => {
    setExportDropdownOpen(prev => ({
      ...prev,
      [key]: false
    }));
  };

  // Render Export Dropdown
  const renderExportDropdown = (data, filename, columns, dropdownKey) => (
    <div className="relative">
      <Button 
        variant="outline" 
        onClick={() => toggleExportDropdown(dropdownKey)}
        className="flex items-center gap-2"
        data-testid={`export-dropdown-btn-${dropdownKey}`}
      >
        <Download className="w-4 h-4" />
        Export
        <ChevronDown className="w-4 h-4" />
      </Button>
      {exportDropdownOpen[dropdownKey] && (
        <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border z-10">
          <button
            onClick={() => { exportToPDF(data, filename, columns); closeExportDropdown(dropdownKey); }}
            className="w-full px-4 py-2 text-left hover:bg-stone-50 flex items-center gap-2"
            data-testid={`export-pdf-btn-${dropdownKey}`}
          >
            <FileText className="w-4 h-4 text-red-500" /> PDF
          </button>
          <button
            onClick={() => { exportToExcel(data, filename); closeExportDropdown(dropdownKey); }}
            className="w-full px-4 py-2 text-left hover:bg-stone-50 flex items-center gap-2"
            data-testid={`export-excel-btn-${dropdownKey}`}
          >
            <FileSpreadsheet className="w-4 h-4 text-green-600" /> Excel
          </button>
          <button
            onClick={() => { exportToCSV(data, filename); closeExportDropdown(dropdownKey); }}
            className="w-full px-4 py-2 text-left hover:bg-stone-50 flex items-center gap-2"
            data-testid={`export-csv-btn-${dropdownKey}`}
          >
            <FileType className="w-4 h-4 text-blue-500" /> CSV
          </button>
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-lime-700"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-stone-800">Inventory Reports</h2>
          <p className="text-sm text-stone-500">
            {viewAllWarehouses ? 'Viewing data across ALL warehouses' : 'Current warehouse inventory analysis'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={viewAllWarehouses}
              onChange={(e) => setViewAllWarehouses(e.target.checked)}
              className="rounded border-stone-300 text-lime-600 focus:ring-lime-500"
              data-testid="all-warehouses-toggle"
            />
            <span className="text-sm font-medium text-stone-700">View All Warehouses</span>
          </label>
        </div>
      </div>

      {/* All Warehouses Summary Panel */}
      {viewAllWarehouses && allWarehousesData && (
        <Card className="border-2 border-lime-200 bg-lime-50">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Package className="w-5 h-5 text-lime-700" />
              All Warehouses Summary
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingAllWarehouses ? (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-lime-700"></div>
              </div>
            ) : (
              <div className="grid grid-cols-5 gap-4">
                <div className="text-center p-3 bg-white rounded-lg border">
                  <p className="text-2xl font-bold text-stone-800">{allWarehousesData.summary.totals?.total_warehouses || 0}</p>
                  <p className="text-xs text-stone-500">Warehouses</p>
                </div>
                <div className="text-center p-3 bg-white rounded-lg border">
                  <p className="text-2xl font-bold text-blue-600">{(allWarehousesData.summary.totals?.total_opening_stock || 0).toFixed(0)}</p>
                  <p className="text-xs text-stone-500">Opening Stock (kg)</p>
                </div>
                <div className="text-center p-3 bg-white rounded-lg border">
                  <p className="text-2xl font-bold text-lime-600">+{(allWarehousesData.summary.totals?.total_stock_in || 0).toFixed(0)}</p>
                  <p className="text-xs text-stone-500">Total Stock In (kg)</p>
                </div>
                <div className="text-center p-3 bg-white rounded-lg border">
                  <p className="text-2xl font-bold text-rose-600">-{(allWarehousesData.summary.totals?.total_stock_out || 0).toFixed(0)}</p>
                  <p className="text-xs text-stone-500">Total Stock Out (kg)</p>
                </div>
                <div className="text-center p-3 bg-white rounded-lg border">
                  <p className="text-2xl font-bold text-amber-600">{(allWarehousesData.summary.totals?.total_current_stock || 0).toFixed(0)}</p>
                  <p className="text-xs text-stone-500">Total Current Stock (kg)</p>
                </div>
              </div>
            )}
            
            {/* Per-Warehouse Breakdown */}
            {!loadingAllWarehouses && allWarehousesData.summary.warehouses?.length > 0 && (
              <div className="mt-4">
                <p className="text-sm font-medium text-stone-600 mb-2">Per-Warehouse Breakdown:</p>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Warehouse</TableHead>
                        <TableHead className="text-left">Opening</TableHead>
                        <TableHead className="text-left">Stock In</TableHead>
                        <TableHead className="text-left">Stock Out</TableHead>
                        <TableHead className="text-left">Current</TableHead>
                        <TableHead className="text-left">Transactions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {allWarehousesData.summary.warehouses.map((ws, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-medium">{ws.warehouse}</TableCell>
                          <TableCell className="text-left text-blue-600">{(ws.opening_stock || 0).toFixed(2)}</TableCell>
                          <TableCell className="text-left text-lime-600">+{(ws.total_in || 0).toFixed(2)}</TableCell>
                          <TableCell className="text-left text-rose-600">-{(ws.total_out || 0).toFixed(2)}</TableCell>
                          <TableCell className="text-left font-bold">{(ws.current_stock || 0).toFixed(2)}</TableCell>
                          <TableCell className="text-left">{ws.transaction_count || 0}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Tabs value={activeReport} onValueChange={setActiveReport} className="w-full">
        <TabsList className="grid grid-cols-5 w-full max-w-3xl">
          <TabsTrigger value="current-stock" className="flex items-center gap-1">
            <Package className="w-4 h-4" />
            Current Stock
          </TabsTrigger>
          <TabsTrigger value="product-wise" className="flex items-center gap-1">
            <TrendingUp className="w-4 h-4" />
            Product-wise
          </TabsTrigger>
          <TabsTrigger value="vendor-wise" className="flex items-center gap-1">
            <Users className="w-4 h-4" />
            Vendor-wise
          </TabsTrigger>
          <TabsTrigger value="low-stock" className="flex items-center gap-1">
            <AlertTriangle className="w-4 h-4" />
            Low Stock
          </TabsTrigger>
          <TabsTrigger value="date-wise" className="flex items-center gap-1">
            <Calendar className="w-4 h-4" />
            Date-wise
          </TabsTrigger>
        </TabsList>

        {/* Current Stock Report */}
        <TabsContent value="current-stock" className="space-y-4">
          <Card>
            <CardHeader className="pb-4">
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Current Stock Report</CardTitle>
                  <CardDescription>Overview of all products with opening, in, out, and current balance</CardDescription>
                </div>
                <div className="flex items-center gap-3">
                  <SearchInput 
                    value={searchTerm} 
                    onChange={setSearchTerm} 
                    placeholder="Search products..." 
                    className="w-64"
                  />
                  {renderExportDropdown(
                    currentStockData.map(d => ({
                      'Product Name': d.product_name,
                      'Category': d.category,
                      'Opening Stock': d.opening_stock,
                      'Total In': d.total_in,
                      'Total Out': d.total_out,
                      'Current Balance': d.current_balance
                    })),
                    "Current_Stock_Report",
                    [
                      { key: 'product_name', label: 'Product Name' },
                      { key: 'category', label: 'Category' },
                      { key: 'opening_stock', label: 'Opening Stock' },
                      { key: 'total_in', label: 'Total In' },
                      { key: 'total_out', label: 'Total Out' },
                      { key: 'current_balance', label: 'Current Balance' }
                    ],
                    'current-stock'
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <SortableHeader column="product_name" label="Product Name" sortConfig={sortConfig} onSort={setSortConfig} />
                    <SortableHeader column="category" label="Category" sortConfig={sortConfig} onSort={setSortConfig} />
                    <SortableHeader column="opening_stock" label="Opening Stock" sortConfig={sortConfig} onSort={setSortConfig} className="text-left" />
                    <SortableHeader column="total_in" label="Total Stock In" sortConfig={sortConfig} onSort={setSortConfig} className="text-left" />
                    <SortableHeader column="total_out" label="Total Stock Out" sortConfig={sortConfig} onSort={setSortConfig} className="text-left" />
                    <SortableHeader column="current_balance" label="Current Balance" sortConfig={sortConfig} onSort={setSortConfig} className="text-left" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {currentStockData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-stone-500">
                        No inventory data available.
                      </TableCell>
                    </TableRow>
                  ) : (
                    currentStockData.map((item, idx) => (
                      <TableRow key={item.id || idx}>
                        <TableCell className="font-medium">{item.product_name}</TableCell>
                        <TableCell>
                          <span className="px-2 py-1 rounded-full text-xs bg-amber-100 text-amber-800">
                            {item.category}
                          </span>
                        </TableCell>
                        <TableCell className="text-left text-blue-600">{item.opening_stock.toFixed(2)}</TableCell>
                        <TableCell className="text-left text-lime-600">{item.total_in.toFixed(2)}</TableCell>
                        <TableCell className="text-left text-rose-600">{item.total_out.toFixed(2)}</TableCell>
                        <TableCell className="text-left font-bold">{item.current_balance.toFixed(2)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
          <p className="text-sm text-stone-500">Showing {currentStockData.length} products</p>
        </TabsContent>

        {/* Product-wise Report */}
        <TabsContent value="product-wise" className="space-y-4">
          <Card>
            <CardHeader className="pb-4">
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Product-wise Stock Report</CardTitle>
                  <CardDescription>Detailed analysis for a specific product</CardDescription>
                </div>
                <div className="flex items-center gap-3">
                  <Select value={productFilter} onValueChange={setProductFilter}>
                    <SelectTrigger className="w-64" data-testid="product-filter-select">
                      <SelectValue placeholder="Select a product" />
                    </SelectTrigger>
                    <SelectContent>
                      {products.map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.product_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {!productFilter ? (
                <div className="p-8 text-center text-stone-500">
                  <Package className="w-12 h-12 mx-auto mb-4 text-stone-300" />
                  Select a product to view its detailed stock report
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Product Name</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead className="text-left">Opening Qty</TableHead>
                      <TableHead className="text-left">Purchases</TableHead>
                      <TableHead className="text-left">Sales</TableHead>
                      <TableHead className="text-left">Adjustments</TableHead>
                      <TableHead className="text-left">Closing Qty</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {productWiseData.map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-medium">{item.product_name}</TableCell>
                        <TableCell>{item.category}</TableCell>
                        <TableCell className="text-left">{item.opening_quantity.toFixed(2)}</TableCell>
                        <TableCell className="text-left text-lime-600">+{item.purchases.toFixed(2)}</TableCell>
                        <TableCell className="text-left text-rose-600">-{item.sales.toFixed(2)}</TableCell>
                        <TableCell className="text-left">{item.adjustments.toFixed(2)}</TableCell>
                        <TableCell className="text-left font-bold">{item.closing_quantity.toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Vendor-wise Report */}
        <TabsContent value="vendor-wise" className="space-y-4">
          <Card>
            <CardHeader className="pb-4">
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Vendor-wise Inventory Report</CardTitle>
                  <CardDescription>Inventory purchased from each vendor</CardDescription>
                </div>
                <div className="flex items-center gap-3">
                  <SearchInput 
                    value={searchTerm} 
                    onChange={setSearchTerm} 
                    placeholder="Search vendors..." 
                    className="w-64"
                  />
                  <Select value={vendorFilter || 'all'} onValueChange={(val) => setVendorFilter(val === 'all' ? '' : val)}>
                    <SelectTrigger className="w-48" data-testid="vendor-filter-select">
                      <SelectValue placeholder="All Vendors" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Vendors</SelectItem>
                      {vendors.map(v => (
                        <SelectItem key={v.id} value={v.id}>{v.vendor_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {renderExportDropdown(
                    vendorWiseData.map(d => ({
                      'Vendor Name': d.vendor_name,
                      'Total Quantity': d.total_quantity,
                      'Products': d.products,
                      'Transactions': d.transaction_count
                    })),
                    "Vendor_Wise_Report",
                    [
                      { key: 'vendor_name', label: 'Vendor Name' },
                      { key: 'total_quantity', label: 'Total Quantity' },
                      { key: 'product_count', label: 'Products' },
                      { key: 'transaction_count', label: 'Transactions' }
                    ],
                    'vendor-wise'
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <SortableHeader column="vendor_name" label="Vendor Name" sortConfig={sortConfig} onSort={setSortConfig} />
                    <SortableHeader column="total_quantity" label="Total Quantity (kg)" sortConfig={sortConfig} onSort={setSortConfig} className="text-left" />
                    <TableHead>Products</TableHead>
                    <SortableHeader column="transaction_count" label="Transactions" sortConfig={sortConfig} onSort={setSortConfig} className="text-left" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {vendorWiseData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center py-8 text-stone-500">
                        No vendor data available.
                      </TableCell>
                    </TableRow>
                  ) : (
                    vendorWiseData.map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-medium">{item.vendor_name}</TableCell>
                        <TableCell className="text-left font-semibold">{item.total_quantity.toFixed(2)}</TableCell>
                        <TableCell className="text-sm text-stone-600 max-w-xs truncate">{item.products}</TableCell>
                        <TableCell className="text-left">{item.transaction_count}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Low Stock Report */}
        <TabsContent value="low-stock" className="space-y-4">
          <Card>
            <CardHeader className="pb-4">
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-500" />
                    Low Stock Alert Report
                  </CardTitle>
                  <CardDescription>Products below minimum stock level ({LOW_STOCK_THRESHOLD} kg)</CardDescription>
                </div>
                <div className="flex items-center gap-3">
                  <SearchInput 
                    value={searchTerm} 
                    onChange={setSearchTerm} 
                    placeholder="Search products..." 
                    className="w-64"
                  />
                  {renderExportDropdown(
                    lowStockData.map(d => ({
                      'Product Name': d.product_name,
                      'Category': d.category,
                      'Current Stock': d.current_stock,
                      'Status': d.current_stock === 0 ? 'Out of Stock' : 'Low Stock'
                    })),
                    "Low_Stock_Report",
                    [
                      { key: 'product_name', label: 'Product Name' },
                      { key: 'category', label: 'Category' },
                      { key: 'current_stock', label: 'Current Stock' }
                    ],
                    'low-stock'
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <SortableHeader column="product_name" label="Product Name" sortConfig={sortConfig} onSort={setSortConfig} />
                    <SortableHeader column="category" label="Category" sortConfig={sortConfig} onSort={setSortConfig} />
                    <SortableHeader column="current_stock" label="Current Stock (kg)" sortConfig={sortConfig} onSort={setSortConfig} className="text-left" />
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lowStockData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center py-8 text-stone-500">
                        <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-lime-500" />
                        All products are well stocked!
                      </TableCell>
                    </TableRow>
                  ) : (
                    lowStockData.map((item, idx) => (
                      <TableRow key={item.id || idx} className="bg-amber-50/50">
                        <TableCell className="font-medium">{item.product_name}</TableCell>
                        <TableCell>{item.category}</TableCell>
                        <TableCell className="text-left font-bold text-amber-600">{item.current_stock.toFixed(2)}</TableCell>
                        <TableCell>
                          {item.current_stock === 0 ? (
                            <span className="px-2 py-1 rounded-full text-xs bg-rose-100 text-rose-800 border border-rose-200">
                              Out of Stock
                            </span>
                          ) : (
                            <span className="px-2 py-1 rounded-full text-xs bg-amber-100 text-amber-800 border border-amber-200">
                              Low Stock
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
          <p className="text-sm text-stone-500">
            {lowStockData.length} products below threshold | {lowStockData.filter(d => d.current_stock === 0).length} out of stock
          </p>
        </TabsContent>

        {/* Date-wise Report */}
        <TabsContent value="date-wise" className="space-y-4">
          <Card>
            <CardHeader className="pb-4">
              <div className="flex flex-col gap-4">
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>Date-wise Inventory Movement</CardTitle>
                    <CardDescription>Transaction history within a date range</CardDescription>
                  </div>
                  {renderExportDropdown(
                    dateWiseData.map(d => ({
                      'Date': new Date(d.transaction_date).toLocaleString(),
                      'Product': d.product_name,
                      'Vendor': d.vendor_name,
                      'Type': d.transaction_type.toUpperCase(),
                      'Quantity': d.quantity,
                      'Notes': d.notes || ''
                    })),
                    "Date_Wise_Report",
                    [
                      { key: 'transaction_date', label: 'Date' },
                      { key: 'product_name', label: 'Product' },
                      { key: 'vendor_name', label: 'Vendor' },
                      { key: 'transaction_type', label: 'Type' },
                      { key: 'quantity', label: 'Quantity' }
                    ],
                    'date-wise'
                  )}
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Label>From:</Label>
                    <Input 
                      type="date" 
                      value={startDate} 
                      onChange={(e) => setStartDate(e.target.value)}
                      className="w-40"
                      data-testid="date-filter-start"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Label>To:</Label>
                    <Input 
                      type="date" 
                      value={endDate} 
                      onChange={(e) => setEndDate(e.target.value)}
                      className="w-40"
                      data-testid="date-filter-end"
                    />
                  </div>
                  <SearchInput 
                    value={searchTerm} 
                    onChange={setSearchTerm} 
                    placeholder="Search..." 
                    className="w-64"
                  />
                  {(startDate || endDate) && (
                    <Button variant="outline" onClick={() => { setStartDate(''); setEndDate(''); }}>
                      Clear Dates
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date & Time</TableHead>
                    <TableHead>Product</TableHead>
                    <TableHead>Vendor</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-left">Quantity (kg)</TableHead>
                    <TableHead>Notes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {dateWiseData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-stone-500">
                        No transactions found for the selected period.
                      </TableCell>
                    </TableRow>
                  ) : (
                    dateWiseData.slice(0, 100).map((trans, idx) => (
                      <TableRow key={trans.id || idx}>
                        <TableCell>{new Date(trans.transaction_date).toLocaleString()}</TableCell>
                        <TableCell className="font-medium">{trans.product_name}</TableCell>
                        <TableCell>{trans.vendor_name}</TableCell>
                        <TableCell>
                          {trans.transaction_type === 'in' ? (
                            <span className="px-2 py-1 rounded-full text-xs bg-lime-100 text-lime-800">IN</span>
                          ) : (
                            <span className="px-2 py-1 rounded-full text-xs bg-rose-100 text-rose-800">OUT</span>
                          )}
                        </TableCell>
                        <TableCell className="text-left font-medium">{trans.quantity.toFixed(2)}</TableCell>
                        <TableCell className="text-stone-500">{trans.notes || '-'}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
          <p className="text-sm text-stone-500">
            Showing {Math.min(100, dateWiseData.length)} of {dateWiseData.length} transactions
            {startDate && endDate && ` from ${startDate} to ${endDate}`}
          </p>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default InventoryReports;
